'use client'

import { Accordion } from 'fukku/Accordion'
import { MODAL_TYPE, Modal, type ModalAPI } from 'fukku/Modal'
import { IconDone } from 'icons/components/IconDone'
import { IconTransportL } from 'icons/components/IconTransportL'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import dynamic from 'next/dynamic'
import { useRef } from 'react'
import { Channel } from 'types/masterData'
import { cx } from 'utils/cx'
import { env } from 'utils/envManager'

import { dataTestIds } from '../../constants/dataTestIds/dataTestIds'
import { useAnalytics } from '../../contexts/AnalyticsProvider/AnalyticsProvider'
import { PdpActionEvents } from '../../types/PdpActionEvents'

import styles from './ShippingAndReturnsLegacy.module.scss'
import text from 'fukku/styles/texts.module.scss'

const DynamicShippingAndReturns = dynamic(() =>
	import(
		'shipping-and-returns/components/ShippingAndReturns/ShippingAndReturns'
	).then((mod) => mod.ShippingAndReturns)
)

interface ShippingAndReturnsProps {
	readonly display: 'ACCORDION' | 'MODAL'
	readonly className?: string
}

export function ShippingAndReturnsLegacy({
	display,
	className,
}: ShippingAndReturnsProps): React.ReactNode {
	const {
		country: { hasOnlineSale },
		isShop,
	} = useMasterData()
	const { sendPdpActionEvent } = useAnalytics()

	const { t } = useLabels()
	const modalRef = useRef<ModalAPI>(null)

	if (!hasOnlineSale) {
		return null
	}

	const title = t('pdp.deliveryAndReturn.title')
	const channel = env.NEXT_PUBLIC_CHANNEL as Channel.Shop | Channel.Outlet

	const onShippingAndReturnsClick = (): void => {
		modalRef.current?.show()
		sendPdpActionEvent(PdpActionEvents.SHIPPING_RETURN_INFO)
	}

	const shippingByChannel = {
		[Channel.Shop]: {
			icon: <IconDone color='semantic-success' />,
			label: t('pdp.free.store.delivery'),
		},
		[Channel.Outlet]: {
			icon: <IconTransportL />,
			label: t('pdp.shipments.and.returns'),
		},
	}

	if (display === 'MODAL') {
		return (
			<div className={cx(className)}>
				{shippingByChannel[channel].icon}
				<button
					className={styles.shippingAndReturnsButton}
					onClick={onShippingAndReturnsClick}
					data-testid={dataTestIds['pdp.productInfo.shippingAndReturns']}
				>
					<span className={text.bodyMRegular}>
						{shippingByChannel[channel].label}
					</span>
					{isShop && (
						<span className={cx(text.bodyS, styles.subtitle)}>
							{t('pdp.free.store.delivery.more.info')}
						</span>
					)}
				</button>
				<Modal
					name='shipping-returns-modal'
					ref={modalRef}
					type={MODAL_TYPE.SIDE}
					title={title}
					showTitle
				>
					<DynamicShippingAndReturns
						dataTestId={
							dataTestIds['pdp.productInfo.shippingAndReturns.content']
						}
					/>
				</Modal>
			</div>
		)
	}

	if (display === 'ACCORDION') {
		return (
			<div
				className={cx(className)}
				data-testid={
					dataTestIds['pdp.productInfo.shippingAndReturns.accordion']
				}
			>
				<Accordion
					id='pdp.shipping-and-returns'
					shouldRenderOnExpand
					data={[
						{
							title,
							onExpanded: () =>
								sendPdpActionEvent(PdpActionEvents.SHIPPING_RETURN_INFO),
							shouldScroll: false,
							content: (
								<DynamicShippingAndReturns
									dataTestId={
										dataTestIds['pdp.productInfo.shippingAndReturns.content']
									}
								/>
							),
						},
					]}
				/>
			</div>
		)
	}

	return null
}
